import React, { useMemo, useState } from "react";
import { Linking, ScrollView, Image, Pressable } from "react-native";
import i18n from "@microdoing/shared/i18n";
import {
  Box,
  CheckRow,
  MainButton,
  MainText,
} from "../../../components/common";
import { useDispatch, useSelector } from "react-redux";
import {
  getLanguage,
  getProfileSettingsDisc,
} from "@microdoing/shared/services/profileSettings/selectors";
import { changeUserInfo } from "@microdoing/shared/services/profileSettings/actions";
import AnimatedBox from "app/components/common/AnimatedBox";
import { SeparatorLine } from "app/components/common/SeparatorLine";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsDiscScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import SettingsHeader from "../../Settings/SettingsHeader";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { SafeAreaView } from "react-native-safe-area-context";
import redDisc from "./assets/red.png";
import yellowDisc from "./assets/yellow.png";
import blueDisc from "./assets/blue.png";
import greenDisc from "./assets/green.png";

const DISC_OPTIONS = [
  {
    key: "disc-red",
    label: i18n.t("dominant"),
    color: i18n.t("red"),
    description: i18n.t("disc-red-description"),
    image: redDisc,
  },
  {
    key: "disc-yellow",
    label: i18n.t("influent"),
    color: i18n.t("yellow"),
    description: i18n.t("disc-yellow-description"),
    image: yellowDisc,
  },
  {
    key: "disc-blue",
    label: i18n.t("compliant"),
    color: i18n.t("blue"),
    description: i18n.t("disc-blue-description"),
    image: blueDisc,
  },
  {
    key: "disc-green",
    label: i18n.t("stable"),
    color: i18n.t("green"),
    description: i18n.t("disc-green-description"),
    image: greenDisc,
  },
];

const Row = ({
  onPress,
  label,
  description,
  image,
  isSelected,
  color = false,
}) => {
  const [isTruncated, setIsTruncated] = useState(true);

  const toggleTruncate = () => {
    setIsTruncated(!isTruncated);
  };

  return (
    <Pressable onPress={onPress}>
      <Box
        backgroundColor={isSelected ? "brand.50" : "neutral.white"}
        paddingVertical="s"
        borderRadius={normalize(8)}
      >
        <Box flexDirection="row" alignItems="center" paddingHorizontal="xm">
          <Box flex={1} marginRight="sm">
            <Box flexDirection="row" alignItems="center">
              <MainText variant="l5-bold" color="neutral.900" marginRight="xs">
                {label}
              </MainText>
              <MainText variant="l3-bold" color="brand.500">
                {color}
              </MainText>
            </Box>
            <MainText variant="l3-medium" color="neutral.600">
              {isTruncated
                ? `${description.substring(0, 100)}...`
                : description}
            </MainText>
            {description.length > 100 && (
              <MainText
                variant="l3-medium"
                onPress={toggleTruncate}
                color="brand.500"
              >
                {isTruncated ? " Read more" : " Show less"}
              </MainText>
            )}
          </Box>
          <Box>
            <Image source={image} style={{ width: 80, height: 80 }} />
          </Box>
        </Box>
      </Box>
    </Pressable>
  );
};

const DiscSelectorScreen = ({ navigation }) => {
  const language = useSelector(getLanguage);
  const disc = useSelector(getProfileSettingsDisc);
  const dispatch = useDispatch();

  const discOptions = useMemo(() => {
    return DISC_OPTIONS;
  }, [language]);

  const onChangeDiscScore = disc => () => {
    dispatch(changeUserInfo({ disc }));
  };

  const openDisc = () => {
    if (language === "fr") {
      Linking.openURL("https://www.dividendes.ch/test/test-disc-gratuit/");
    } else {
      Linking.openURL("https://www.123test.com/disc-personality-test/");
    }
  };

  const displayList = () => {
    const listItem = discOptions.map((element, index) => {
      return (
        <Box key={index}>
          <Row
            label={element.label}
            image={element.image}
            description={element.description}
            color={element.color}
            isSelected={element.key === disc}
            onPress={onChangeDiscScore(element.key)}
          />
        </Box>
      );
    });
    return listItem;
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsDiscScreen}>
      <SafeAreaView
        style={{
          flex: 1,
          backgroundColor: theme.colors["neutral.white"],
          width: "100%",
        }}
      >
        <Box backgroundColor="neutral.white" flex={1} paddingHorizontal="m">
          <SettingsHeader
            title={isWebDesktop ? "" : i18n.t("disc-title")}
            leftIcon={
              !isWebDesktop && (
                <ArrowLeft
                  color={theme.colors["neutral.900"]}
                  height={normalize(30)}
                  width={normalize(30)}
                />
              )
            }
          />
          <MainButton
            color="brand.50"
            textColor="brand.500"
            onPress={openDisc}
            text={i18n.t("complete-disc-test")}
          />
          <ScrollView contentContainerStyle={styles.scrollView}>
            {displayList()}
          </ScrollView>
        </Box>
      </SafeAreaView>
    </WebDesktopView>
  );
};

const styles = {
  mainBox: {
    alignSelf: "center",
    width: "100%",
    marginBottom: 15,
    flex: 1,
    paddingHorizontal: 0,
  },
  scrollView: { paddingBottom: 0 },
};

export default DiscSelectorScreen;
